import { useState, useCallback, useMemo } from "react";
import type { DocumentItem } from "@/types/ui-models";
import { useDocumentsList } from "@/hooks";
import { NoRealData } from "@/components/shared";
import DocumentsKpiCard from "./components/DocumentsKpiCard";
import DocumentsFilters from "./components/DocumentsFilters";
import DocumentsTable from "./components/DocumentsTable";
import DocumentCard from "./components/DocumentCard";
import DocumentDetailDrawer from "./components/DocumentDetailDrawer";
import ProjectSourcesPanel from "./components/ProjectSourcesPanel";
import TreeSourcesTable from "./components/TreeSourcesTable";
import EnvironmentalSourcesTable from "./components/EnvironmentalSourcesTable";
import ExpirationPanel from "./components/ExpirationPanel";
import { hasStatus, isExpired, norm, uniqueSorted } from "./lib/docUtils";

const tabs = [
  { id: "biblioteca", label: "Biblioteca" },
  { id: "projetos", label: "Fontes por Projeto" },
  { id: "arvores", label: "Fontes por Exemplar CTA" },
  { id: "ambientais", label: "Fontes Ambientais" },
  { id: "gis", label: "Bases GIS" },
  { id: "autorizacoes", label: "Autorizações e Laudos" },
  { id: "vencimentos", label: "Vencimentos" },
  { id: "relatorios", label: "Relatórios" },
];

const initialFilters = {
  search: "",
  type: "",
  project: "",
  municipality: "",
  status: "",
  hasBinding: "",
  hasFile: "",
  isExpired: "",
};

export default function Documentos() {
  const [activeTab, setActiveTab] = useState("biblioteca");
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);
  const [showCards, setShowCards] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const { items: documents, loading, error } = useDocumentsList();

  const handleViewDocument = useCallback((docOrId: DocumentItem | string) => {
    if (typeof docOrId === "string") {
      const found = documents.find((d) => d.id === docOrId);
      if (found) setSelectedDoc(found);
    } else {
      setSelectedDoc(docOrId);
    }
  }, [documents]);

  const handleCloseDrawer = useCallback(() => {
    setSelectedDoc(null);
  }, []);

  const typeOptions = useMemo(() => uniqueSorted(documents.map((d) => d.type)), [documents]);
  const statusOptions = useMemo(() => uniqueSorted(documents.map((d) => d.status)), [documents]);

  const filteredDocs = useMemo(() => {
    return documents.filter((doc) => {
      if (filters.search) {
        const s = norm(filters.search);
        const matchesSearch =
          norm(doc.title).includes(s) ||
          norm(doc.number).includes(s) ||
          norm(doc.primaryBinding).includes(s) ||
          norm(doc.projectName).includes(s) ||
          norm(doc.issuingBody).includes(s) ||
          norm(doc.description).includes(s);
        if (!matchesSearch) return false;
      }
      if (filters.type && doc.type !== filters.type) return false;
      if (filters.status && doc.status !== filters.status) return false;
      if (filters.isExpired === "yes" && !isExpired(doc)) return false;
      if (filters.isExpired === "no" && isExpired(doc)) return false;
      if (filters.hasFile === "yes" && !doc.hasFile) return false;
      if (filters.hasFile === "no" && doc.hasFile) return false;
      if (filters.hasBinding === "yes" && doc.primaryBindingType === "none") return false;
      if (filters.hasBinding === "no" && doc.primaryBindingType !== "none") return false;
      if (filters.project && doc.projectName !== filters.project) return false;
      return true;
    });
  }, [documents, filters]);

  const validatedDocs = documents.filter((d) => hasStatus(d, "validado")).length;
  const pendingDocs = documents.filter((d) => hasStatus(d, "em revisão", "rascunho")).length;
  const expiredDocs = documents.filter(isExpired).length;
  const withFileDocs = documents.filter((d) => d.hasFile).length;
  const unlinkedDocs = documents.filter((d) => d.primaryBindingType === "none").length;

  const kpi = (n: number) => (loading || error ? "—" : n.toLocaleString("pt-BR"));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Documentos e Fontes</div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
          Documentos e Fontes
        </h1>
        <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
          Organize fontes documentais, laudos, autorizações, bases cartográficas e evidências vinculadas ao CTA, projetos, áreas verdes e responsabilidades ambientais.
        </p>
      </div>

      {/* Conceptual notice */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-secondary-600 w-full h-full flex items-center justify-center text-lg" />
          </div>
          <p className="text-xs text-secondary-700 leading-relaxed">
            Cada documento pode funcionar como <strong>fonte oficial</strong> para exemplares arbóreos, áreas ambientais, vistorias, autorizações, restrições e responsabilidades dentro do modelo <strong>LADM-FloraIQ</strong> — conectando Source (fonte documental), Party (parte envolvida), Spatial Unit (unidade ambiental) e RRR (responsabilidades, restrições e autorizações).
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <DocumentsKpiCard label="Documentos cadastrados" value={kpi(documents.length)} icon="ri-folder-line" color="primary" subtitle="Total no repositório" />
        <DocumentsKpiCard label="Fontes validadas" value={kpi(validatedDocs)} icon="ri-check-double-line" color="primary" subtitle="Documentos conferidos" />
        <DocumentsKpiCard label="Pendentes de revisão" value={kpi(pendingDocs)} icon="ri-search-line" color="warning" subtitle="Rascunho ou em revisão" />
        <DocumentsKpiCard label="Documentos vencidos" value={kpi(expiredDocs)} icon="ri-error-warning-line" color="danger" subtitle="Validade expirada" />
        <DocumentsKpiCard label="Com arquivo anexado" value={kpi(withFileDocs)} icon="ri-attachment-2" color="accent" subtitle="Possuem arquivo" />
        <DocumentsKpiCard label="Documentos sem vínculo" value={kpi(unlinkedDocs)} icon="ri-link-unlink-m" color="warning" subtitle="Precisam ser vinculados" />
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {loading ? (
          <div className="text-center py-12 text-foreground-400 text-sm">Carregando documentos...</div>
        ) : error ? (
          <NoRealData
            title="Não foi possível carregar os documentos"
            description="A consulta ao servidor falhou. Tente novamente em instantes."
            icon="ri-error-warning-line"
          />
        ) : (
          <>
            {/* Biblioteca */}
            {activeTab === "biblioteca" && (
              documents.length === 0 ? (
                <NoRealData
                  title="Nenhum documento cadastrado"
                  description="A biblioteca documental lista os documentos e fontes registrados no banco. Ainda não há nenhum."
                  icon="ri-folder-line"
                />
              ) : (
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground-900">Biblioteca documental</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowCards(false)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                      >
                        <i className="ri-table-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                        Tabela
                      </button>
                      <button
                        onClick={() => setShowCards(true)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                      >
                        <i className="ri-grid-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                        Cards
                      </button>
                    </div>
                  </div>

                  <DocumentsFilters filters={filters} onChange={setFilters} typeOptions={typeOptions} statusOptions={statusOptions} />

                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-filter-line w-4 h-4 flex items-center justify-center" />
                    <span>{filteredDocs.length} de {documents.length} documentos encontrados</span>
                  </div>

                  {filteredDocs.length === 0 ? (
                    <div className="text-center py-12 text-foreground-400">
                      <i className="ri-file-search-line text-4xl mb-3 block" />
                      <p className="text-sm">Nenhum documento encontrado com os filtros atuais.</p>
                    </div>
                  ) : showCards ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredDocs.map((doc) => (
                        <DocumentCard key={doc.id} document={doc} onView={handleViewDocument} />
                      ))}
                    </div>
                  ) : (
                    <DocumentsTable documents={filteredDocs} onView={handleViewDocument} />
                  )}
                </div>
              )
            )}

            {activeTab === "projetos" && (
              <ProjectSourcesPanel documents={documents} onViewDocument={handleViewDocument} />
            )}

            {activeTab === "arvores" && (
              <TreeSourcesTable documents={documents} onViewDocument={handleViewDocument} />
            )}

            {activeTab === "ambientais" && (
              <EnvironmentalSourcesTable documents={documents} onViewDocument={handleViewDocument} />
            )}

            {activeTab === "gis" && (
              <NoRealData
                title="Bases GIS"
                description="Catálogo de bases cartográficas com SRID, geometria, número de feições e qualidade. Ainda não há fonte real: o servidor não expõe metadados GIS dos documentos."
                icon="ri-map-line"
              />
            )}

            {activeTab === "autorizacoes" && (
              <NoRealData
                title="Autorizações e laudos"
                description="Autorizações de poda, supressão e transplante, laudos e termos de compensação, com responsável técnico e validade. Ainda não há fonte real dedicada (RRR não está ligado a esta tela)."
                icon="ri-shield-check-line"
              />
            )}

            {activeTab === "vencimentos" && (
              <ExpirationPanel documents={documents} onViewDocument={handleViewDocument} />
            )}

            {activeTab === "relatorios" && (
              <NoRealData
                title="Relatórios e qualidade documental"
                description="Relatórios documentais e métricas de qualidade do acervo. Ainda não há geração de relatórios nem métricas reais para esta seção."
                icon="ri-file-chart-line"
              />
            )}
          </>
        )}
      </div>

      {/* Drawer */}
      {selectedDoc && (
        <DocumentDetailDrawer document={selectedDoc} onClose={handleCloseDrawer} />
      )}
    </div>
  );
}
